"use client"

import { Package, Square, LayoutGrid, Printer } from "lucide-react"
import { PHASES, PHASE_LABELS, type Phase } from "@/lib/core/phases"

// Sewing-machine LCD readout: four tappable phase labels at the top of the
// chat. The current resolved phase is boxed; tapping any label writes a
// chosen phase to the doc, which the menu and chrome read via resolvePhase.
//
// Status, not navigation in the routing sense — the user is always on one
// page; the strip just tells the machine what kind of work to focus on.

const PHASE_ICONS: Record<Phase, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>> = {
  fabrics: Package,
  blocks: Square,
  blanket: LayoutGrid,
  finishing: Printer,
}

interface PhaseStripProps {
  active: Phase
  onSelect: (phase: Phase) => void
}

export default function PhaseStrip({ active, onSelect }: PhaseStripProps) {
  return (
    <div
      role="tablist"
      aria-label="Work phase"
      className="flex divide-x divide-border overflow-hidden rounded-md border border-border bg-background animate-fade-in"
    >
      {PHASES.map((phase) => {
        const Icon = PHASE_ICONS[phase]
        const isActive = phase === active
        return (
          <button
            key={phase}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(phase)}
            className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 text-base transition-colors ${
              isActive
                ? "bg-foreground text-background"
                : "bg-background text-muted-foreground hover:bg-muted active:bg-muted"
            }`}
          >
            <Icon size={14} strokeWidth={1.75} className="flex-shrink-0" />
            <span className="truncate">{PHASE_LABELS[phase]}</span>
          </button>
        )
      })}
    </div>
  )
}
